import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { FileText, Download, Calendar, User, Import as Passport, Stamp, Trash2 } from 'lucide-react';
import { DocumentStatus } from '../../types/document';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { doc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import toast from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  documentData: DocumentStatus;
  onDelete?: () => void;
};

export const DocumentViewModal = ({ isOpen, onClose, documentData, onDelete }: Props) => {
  const { translations } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // English fallbacks for translations
  const t = {
    downloadDocument: translations?.documentViewModal?.downloadDocument || 'Download Document',
    deleteDocument: translations?.documentViewModal?.deleteDocument || 'Delete Document',
    deleteConfirm: translations?.documentViewModal?.deleteConfirm || 'Are you sure you want to delete this document?',
    deleting: translations?.documentViewModal?.deleting || 'Deleting...',
    deleteSuccess: translations?.documentViewModal?.deleteSuccess || 'Document deleted successfully',
    deleteError: translations?.documentViewModal?.deleteError || 'Failed to delete document',
    pilgrimName: translations?.documentViewModal?.pilgrimName || 'Pilgrim Name',
    passportNumber: translations?.documentViewModal?.passportNumber || 'Passport Number',
    uploadedBy: translations?.documentViewModal?.uploadedBy || 'Uploaded By',
    uploadDate: translations?.documentViewModal?.uploadDate || 'Upload Date',
    fileName: translations?.documentViewModal?.fileName || 'File Name',
    passportDetails: translations?.documentViewModal?.passportDetails || 'Passport Details',
    visaDetails: translations?.documentViewModal?.visaDetails || 'Visa Details'
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentData.fileUrl;
    link.download = documentData.fileName || `${documentData.documentType}-document.${documentData.fileType === 'pdf' ? 'pdf' : 'jpg'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async () => {
    if (!confirm(t.deleteConfirm)) return;
    
    setIsDeleting(true);
    try {
      const documentRef = doc(firestore, 'PilgrimDocuments', documentData.id);
      await deleteDoc(documentRef);
      toast.success(t.deleteSuccess);
      
      if (onDelete) {
        onDelete();
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(t.deleteError);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${documentData.documentType === 'passport' ? 'Passport' : 'Visa'} Document - ${documentData.pilgrimName}`}
      width="90vw"
      maxWidth="1000px"
    >
      <div className="flex flex-col md:flex-row gap-6">
        {/* Document Preview */}
        <div className="flex-1 min-h-[400px] bg-gray-100 rounded-lg overflow-hidden">
          {documentData.fileType === 'pdf' ? (
            <iframe
              src={`${documentData.fileUrl}#view=FitH`}
              className="w-full h-[600px]"
              title={`${documentData.documentType} for ${documentData.pilgrimName}`}
              onLoad={() => setIsLoading(false)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
                </div>
              )}
              <img
                src={documentData.fileUrl}
                alt={`${documentData.documentType} for ${documentData.pilgrimName}`}
                className="max-w-full max-h-full object-contain"
                onLoad={() => setIsLoading(false)}
              />
            </div>
          )}
        </div>

        {/* Document Info */}
        <div className="w-full md:w-[320px] bg-gray-50 rounded-lg p-6 space-y-6">
          <div>
            <h3 className="font-semibold text-lg mb-4 flex items-center">
              {documentData.documentType === 'passport' ? (
                <Passport className="text-primary-500 mr-2" size={24} />
              ) : (
                <Stamp className="text-primary-500 mr-2" size={24} />
              )}
              {documentData.documentType === 'passport' ? t.passportDetails : t.visaDetails}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">{t.pilgrimName}</label>
                <div className="mt-1 text-sm">{documentData.pilgrimName}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">{t.passportNumber}</label>
                <div className="mt-1 text-sm">{documentData.passportNumber}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">{t.uploadedBy}</label>
                <div className="mt-1 text-sm flex items-center">
                  <User size={16} className="text-gray-400 mr-1" />
                  <span>{documentData.uploadedByName || documentData.uploadedBy || 'Unknown'}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">{t.uploadDate}</label>
                <div className="mt-1 text-sm flex items-center">
                  <Calendar size={16} className="text-gray-400 mr-1" />
                  <span>{new Date(documentData.uploadedAt).toLocaleString()}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">{t.fileName}</label>
                <div className="mt-1 text-sm text-gray-900 truncate" title={documentData.fileName}>
                  <FileText size={16} className="text-gray-400 inline mr-1" />
                  {documentData.fileName}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownload}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Download size={20} />
              <span>{t.downloadDocument}</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-red-300 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <Trash2 size={20} />
              )}
              <span>{isDeleting ? t.deleting : t.deleteDocument}</span>
            </motion.button>
          </div>
        </div>
      </div>
    </Modal>
  );
};